import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Tabs, Spin, Button, Descriptions, Table, Tag, Empty } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import {
  customerService,
  Customer,
  CustomerWallet,
  CustomerTransaction,
  CustomerIdentifier,
} from '../../services/customerService';
import './CustomerProfile.scss';

const { TabPane } = Tabs;

const CustomerProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [wallets, setWallets] = useState<CustomerWallet[]>([]);
  const [transactions, setTransactions] = useState<CustomerTransaction[]>([]);
  const [identifiers, setIdentifiers] = useState<CustomerIdentifier[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    if (id) {
      fetchCustomerData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchCustomerData = async () => {
    setLoading(true);
    try {
      const customerId = parseInt(id!);
      const customerData = await customerService.getCustomerById(customerId);
      setCustomer(customerData);

      const walletsData = await customerService.getCustomerWallets(customerId);
      setWallets(walletsData);

      const identifiersData = await customerService.getCustomerIdentifiers(customerId);
      setIdentifiers(identifiersData);

      if (walletsData.length > 0) {
        const transactionsData = await customerService.getCustomerTransactions(
          walletsData[0].walletId
        );
        setTransactions(transactionsData);
      }
    } catch (error) {
      console.error('Failed to fetch customer data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/customers')}>
          Back to Customers
        </Button>
        <Empty description="Customer not found" />
      </div>
    );
  }

  const walletColumns = [
    {
      title: 'Wallet ID',
      dataIndex: 'walletId',
      key: 'walletId',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color = status === 'ACTIVE' ? 'green' : status === 'SUSPENDED' ? 'orange' : 'red';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      render: (balance: number, record: CustomerWallet) =>
        `${balance.toFixed(2)} ${record.currency}`,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleString(),
    },
  ];

  const transactionColumns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        const color = type === 'CREDIT' ? 'green' : 'red';
        return <Tag color={color}>{type}</Tag>;
      },
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: CustomerTransaction) =>
        `${amount.toFixed(2)} ${record.currency}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag>{status}</Tag>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleString(),
    },
  ];

  const identifierColumns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
    },
    {
      title: 'Primary',
      dataIndex: 'isPrimary',
      key: 'isPrimary',
      render: (isPrimary: boolean) => (isPrimary ? <Tag color="blue">Primary</Tag> : '-'),
    },
    {
      title: 'Verified',
      dataIndex: 'verifiedAt',
      key: 'verifiedAt',
      render: (verifiedAt: string) =>
        verifiedAt ? <Tag color="green">Verified</Tag> : <Tag>Not Verified</Tag>,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleString(),
    },
  ];

  return (
    <div className="customer-profile-page">
      <div className="page-header">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/customers')}>
          Back to Customers
        </Button>
        <h2>
          Customer Profile: {customer.firstName} {customer.lastName}
        </h2>
      </div>

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Profile" key="profile">
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Customer ID">{customer.id}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag
                  color={
                    customer.status === 'ACTIVE'
                      ? 'green'
                      : customer.status === 'SUSPENDED'
                      ? 'red'
                      : 'default'
                  }
                >
                  {customer.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="First Name">{customer.firstName}</Descriptions.Item>
              <Descriptions.Item label="Last Name">{customer.lastName}</Descriptions.Item>
              <Descriptions.Item label="Phone Number">
                {customer.phoneNumber || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Email">{customer.email || '-'}</Descriptions.Item>
              <Descriptions.Item label="KYC Level">
                {customer.kycLevel || 'ANONYMOUS'}
              </Descriptions.Item>
              <Descriptions.Item label="Preferred Language">
                {customer.preferredLanguage || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {new Date(customer.createdAt).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Updated At">
                {new Date(customer.updatedAt).toLocaleString()}
              </Descriptions.Item>
            </Descriptions>
          </TabPane>

          <TabPane tab="Wallets" key="wallets">
            <Table
              columns={walletColumns}
              dataSource={wallets}
              rowKey="id"
              pagination={false}
              locale={{ emptyText: 'No wallets found' }}
            />
          </TabPane>

          <TabPane tab="Transactions" key="transactions">
            <Table
              columns={transactionColumns}
              dataSource={transactions}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: 'No transactions found' }}
            />
          </TabPane>

          <TabPane tab="Identifiers" key="identifiers">
            <Table
              columns={identifierColumns}
              dataSource={identifiers}
              rowKey="id"
              pagination={false}
              locale={{ emptyText: 'No identifiers found' }}
            />
          </TabPane>

        </Tabs>
      </Card>
    </div>
  );
};

export default CustomerProfilePage;
